/**
 * ATD (approved to dispense) for one resident: the ONE place ATD is marked, shared by the Finance dossier, the Finance
 * Kanban's card dock, the UMED case panel and "ATD — all residents" (Finance and UMED).
 *
 * Isaiah Cotto, 2026-10-15: "Why is marking ATDs so complicated? We should have a button that marks it ATD in finance
 * and moves the record to the ATD tab in scheduling so they can be put on the manifest." So the panel leads with ONE
 * primary action:
 *
 *   Mark ATD  ->  a confirmation naming the resident, the building and the ATD date (today, editable)
 *            ->  POST /atd/residents/:id { to_status: 'Yes' } with an Idempotency-Key (the one command; one
 *                transaction that also leaves the resident on Scheduling's ATD list)
 *            ->  "Marked ATD; now in Scheduling's ATD list." and an Undo (the governed reversal: Yes -> No, reason).
 *
 * The screenshot of 2026-10-15 showed the old panel as plain text: Tailwind's preflight strips every <button> and
 * <input> to bare text, and atd.css styled only the layout, so "Mark ATD" (a heading) sat above "Mark ATD" (a button
 * that looked like a word). atd.css now draws real buttons, a labelled date and a confirmation.
 *
 * The server says which moves THIS caller may make (`allowed_for_you`); the panel offers exactly those and never works
 * out a transition itself. A caller with neither ATD permission (403) sees nothing. Blank is "No ATD recorded", never
 * "No". A resident with no confirmed building is refused by the server ("Set the building first"); the confirmation
 * says so before anyone presses. A building closed for scheduling is marked (ruling 2026-10-14), and said.
 */
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { atdApi, newAtdKey, AtdResident, AtdMove, AtdNotice, AtdDispensePlan, AtdHistoryRow, AtdChangeOutcome, AtdClosure } from './atdApi';
import BuildingOrClinic from '../emerge/BuildingOrClinic';
import './atd.css';

const NOT_ON = 'ATD changes are not switched on yet.';
const LIST = "Scheduling's ATD list";
const SET_BUILDING = 'Set the building first (Manifest Review, No building).';

const NOTICE_WHO: Record<AtdNotice['kind'], string> = { PROVIDER: 'Provider', SCHEDULING: 'Scheduling' };
function noticeWords(n: AtdNotice): string {
  switch (n.outcome) {
    case 'SENT': return `emailed${n.recipients ? ` (${n.recipients} ${n.recipients === 1 ? 'recipient' : 'recipients'})` : ''}`;
    case 'SINK': return 'recorded (test mail sink)';
    case 'SKIPPED': return `not sent: ${n.reason || 'no reason recorded'}`;
    default: return `failed: ${n.reason || 'no reason recorded'}`;
  }
}
const PLAN_ATTENTION: Record<NonNullable<AtdDispensePlan['attention']>, string> = {
  VISIT_OTHER_BUILDING: 'it was planned on another building\'s visit; the resident has moved',
  VISIT_CANCELLED: 'that visit was cancelled',
  VISIT_COMPLETED: 'that visit completed without a dispense recorded',
  VISIT_PASSED: 'that visit date passed without a dispense recorded',
  PIT_STOP_PASSED: 'that pit stop date passed',
};
function planWords(p: AtdDispensePlan): string {
  const words = p.state === 'ON_MANIFEST' ? `On the manifest for ${p.visit_on || 'a date not recorded'}`
    : p.state === 'PIT_STOP' ? `Pit stop ${p.pit_stop_on || 'date not recorded'}`
      : 'Not yet scheduled';
  return p.attention ? `${words} (${PLAN_ATTENTION[p.attention]})` : words;
}
const closedWords = (c: AtdClosure) => `This building is closed for scheduling (${c.words}), so they cannot be put on a manifest or a pit stop there.`;
const refusal = (r: { status?: number; code?: string; message?: string; error?: string }) =>
  (r.status === 404 && r.code !== 'NOT_FOUND' ? NOT_ON : (r.message || r.error || 'That did not go through.'));

/**
 * What the Mark ATD result says, from the server's answer: the act, then Scheduling. Exported for the boards' status
 * lines and the tests.
 */
export function markResultWords(out: AtdChangeOutcome, since: string | null = null): string {
  const s = out.scheduling;
  const onList = s ? s.in_atd_list : true;
  const closed = s && s.building_closed ? ` ${closedWords(s.building_closed)}` : '';
  /*
   * No confirmed building (verification of #423, low): a resident ALREADY Yes without one (marked before 2026-10-15,
   * or from Monday) is on the list's predicate but can never be planned, and a building-scoped scheduler never sees
   * them. Say that, not "in Scheduling's ATD list". Known only from a server that sends `building` (absent: as before).
   */
  const noBuilding = out.building === null;
  const head = out.unchanged ? `Already marked ATD${since ? ` (since ${since})` : ''}`
    : out.replayed ? 'That was already recorded: marked ATD' : 'Marked ATD';
  if (noBuilding) return `${head}, but no building is set, so Scheduling can't put them on a manifest. ${SET_BUILDING}`;
  const listed = onList ? `${out.unchanged || out.replayed ? 'in' : 'now in'} ${LIST}` : `not in ${LIST}`;
  return `${head}; ${listed}.${closed}`;
}

const UNRECORDED = 'A notice was sent, but eMerge could not record it. Do not resend until engineering has fixed this: another resend would send it again.';
const NOT_RECORDABLE = 'eMerge cannot record notices right now, so it stopped before sending the rest. Tell engineering, then try again later.';

/** Resend notices: only to whoever has not been told; the answer says what was attempted. */
function ResendNotices({ patientId, onDone }: { patientId: string; onDone: (msg: string) => Promise<void> | void }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const run = async () => {
    if (busy) return;
    setBusy(true); setErr(null);
    const r = await atdApi.resend(patientId);
    setBusy(false);
    if (!r.success) { setErr(refusal(r)); return; }
    // The database would not take the record (PR #419 round 3): say what really happened, and what not to do.
    if (r.data?.halted === 'UNRECORDED_DELIVERY') { setErr(UNRECORDED); return; }
    if (r.data?.halted === 'NOT_RECORDABLE') { setErr(NOT_RECORDABLE); return; }
    const sent = (r.data?.notices || []).filter((n) => n.outcome === 'SENT' || n.outcome === 'SINK').length;
    const tried = (r.data?.notices || []).length;
    await onDone(r.data?.skipped === 'IN_PROGRESS' ? 'Notices are being sent right now; check again in a moment.'
      : tried === 0 ? 'Everyone has already been told; nothing was sent.'
        : `Resent: ${sent} of ${tried} delivered.`);
  };
  return (
    <div className="atd-resend">
      <button type="button" className="atd-btn" onClick={run} disabled={busy}>{busy ? 'Sending…' : 'Resend notices'}</button>
      <span className="atd-quiet"> Sends only to whoever has not been told yet.</span>
      {err && <p className="atd-err" role="alert">{err}</p>}
    </div>
  );
}
const dayOf = (s: string | null) => (s ? s.slice(0, 10) : 'date not recorded');

/**
 * Mark ATD: the one primary button, then a confirmation that names the resident, the building and the date. Nothing
 * is recorded until "Confirm: mark ATD". One Idempotency-Key per confirmation, kept for a retry the server never
 * answered (a lost answer replays, never a second act).
 */
function MarkAtd({ d, open, setOpen, onDone }: {
  d: AtdResident; open: boolean; setOpen: (v: boolean) => void;
  onDone: (msg: string, out: AtdChangeOutcome | null) => Promise<void> | void;
}) {
  const uid = useId().replace(/:/g, '');
  const [date, setDate] = useState(d.today);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const key = useRef(newAtdKey());
  const dateRef = useRef<HTMLInputElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const name = d.resident.name || 'this resident';
  const closure = d.facility ? d.facility.closure || null : null;
  useEffect(() => { if (open) window.setTimeout(() => dateRef.current?.focus(), 0); }, [open]);

  const cancel = () => { setOpen(false); setErr(null); window.setTimeout(() => triggerRef.current?.focus(), 0); };
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setErr(null);
    const r = await atdApi.change(d.patient_id, { to_status: 'Yes', ...(date ? { effective_date: date } : {}) }, key.current);
    setBusy(false);
    if (r.status !== undefined || r.success) key.current = newAtdKey(); // answered: the next confirmation is a new act
    if (!r.success) { setErr(refusal(r)); return; }
    setOpen(false);
    const out = r.data || {};
    await onDone(markResultWords(out, out.atd_date || null), out);
  };

  return (
    <div className="atd-mark">
      <button type="button" ref={triggerRef} className="atd-btn atd-btn--primary" aria-expanded={open} aria-controls={`atd-confirm-${uid}`}
        onClick={() => { setErr(null); setOpen(!open); }}>
        Mark ATD
      </button>
      {open && (
        <form id={`atd-confirm-${uid}`} className="atd-confirm" onSubmit={submit} aria-label={`Confirm Mark ATD for ${name}`}>
          <p className="atd-confirm-what">Mark <strong>{name}</strong> approved to dispense?</p>
          <dl className="atd-facts">
            <div><dt>Resident</dt><dd>{name}</dd></div>
            <div><dt>Building</dt><dd>{d.facility
              ? (d.facility.name || 'Building name not recorded')
              : <BuildingOrClinic row={{ counselear_clinic_id: d.counselear_clinic?.id ?? null, counselear_clinic_name: d.counselear_clinic?.name ?? null }} fallback="No building on file" />}</dd></div>
          </dl>
          {!d.facility ? (
            <p className="atd-err" role="alert">
              Set the building first: eMerge has no confirmed building for {name}, so Scheduling could not put them on a manifest.
              Confirm their building in Manifest Review (No building), then mark ATD.
            </p>
          ) : (
            <>
              {closure && <p className="atd-warn" role="note">{closedWords(closure)} ATD is still recorded.</p>}
              <label className="atd-field" htmlFor={`atd-date-${uid}`}>
                <span>ATD date</span>
                <input id={`atd-date-${uid}`} ref={dateRef} type="date" value={date} max={d.today} required onChange={(e) => setDate(e.target.value)} />
              </label>
              <p className="atd-quiet">
                Records ATD = Yes, tells the provider and Scheduling, and puts {name} on {LIST}{closure ? '' : ' to go on the next manifest'}.
              </p>
            </>
          )}
          {err && <p className="atd-err" role="alert">{err}</p>}
          <div className="atd-btns">
            {d.facility && <button type="submit" className="atd-btn atd-btn--primary" disabled={busy}>{busy ? 'Marking…' : 'Confirm: mark ATD'}</button>}
            <button type="button" className="atd-btn" onClick={cancel} disabled={busy}>Cancel</button>
          </div>
        </form>
      )}
    </div>
  );
}

/** A secondary act behind its own button: the form shows only once asked for. */
function MoveForm({ patientId, today, moves, title, cta, defaultToday, pickWord = false, onDone }: {
  patientId: string; today: string; moves: AtdMove[]; title: string; cta: string; defaultToday: boolean; pickWord?: boolean;
  onDone: (msg: string) => Promise<void> | void;
}) {
  const uid = useId().replace(/:/g, '');
  const [open, setOpen] = useState(false);
  const [to, setTo] = useState(moves[0].to);
  const [date, setDate] = useState(defaultToday ? today : '');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const key = useRef(newAtdKey());
  const move = moves.find((m) => m.to === to) || moves[0];
  const id = `atd-${uid}`;
  const dateLabel = move.date === 'ATD_DATE' ? 'ATD date' : move.date === 'DISPENSE_DATE' ? 'Dispense date' : 'Effective date (optional)';

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (move.reason_required && !note.trim()) { setErr('A reason is required for this change.'); return; }
    setBusy(true); setErr(null);
    const body: { to_status: string; effective_date?: string; note?: string } = { to_status: move.to };
    if (date) body.effective_date = date;
    if (note.trim()) body.note = note.trim();
    const r = await atdApi.change(patientId, body, key.current);
    setBusy(false);
    if (r.status !== undefined || r.success) key.current = newAtdKey(); // answered: the next submit is a new act
    if (!r.success) { setErr(refusal(r)); return; }
    setNote(''); setOpen(false);
    const out = r.data || {};
    await onDone(out.unchanged ? `Already ${move.to}; nothing changed.` : out.replayed ? `That was already recorded (${move.to}).` : `Recorded: ${move.to}${date ? ` · ${date}` : ''}.`);
  };

  return (
    <div className="atd-move">
      <button type="button" className="atd-btn" aria-expanded={open} aria-controls={`${id}-form`} onClick={() => { setErr(null); setOpen(!open); }}>{title}</button>
      {open && (
        <form id={`${id}-form`} className="atd-confirm atd-confirm--quiet" onSubmit={submit} aria-label={title}>
          {pickWord && (
            <label className="atd-field" htmlFor={`${id}-to`}><span>New word</span>
              <select id={`${id}-to`} value={to} onChange={(e) => { setTo(e.target.value); setErr(null); }}>
                {moves.map((m) => <option key={m.to} value={m.to}>{m.to}</option>)}
              </select>
            </label>
          )}
          {move.meaning && <p className="atd-quiet">{move.meaning}</p>}
          <label className="atd-field" htmlFor={`${id}-date`}><span>{dateLabel}</span>
            <input id={`${id}-date`} type="date" value={date} max={today} onChange={(e) => setDate(e.target.value)} />
          </label>
          {(move.reason_required || pickWord) && (
            <label className="atd-field" htmlFor={`${id}-note`}><span>{move.reason_required ? 'Reason (required)' : 'Note (optional)'}</span>
              <textarea id={`${id}-note`} rows={2} value={note} onChange={(e) => setNote(e.target.value)} required={move.reason_required} />
            </label>
          )}
          {err && <p className="atd-err" role="alert">{err}</p>}
          <div className="atd-btns">
            <button type="submit" className="atd-btn atd-btn--primary" disabled={busy}>{busy ? 'Saving…' : cta}</button>
            <button type="button" className="atd-btn" onClick={() => { setOpen(false); setErr(null); }} disabled={busy}>Cancel</button>
          </div>
        </form>
      )}
    </div>
  );
}

/**
 * Undo a Mark ATD just made here: the governed reversal, the same command (Yes -> the word it had, or No), with the
 * reason the command requires. The history keeps both rows.
 */
function UndoMark({ patientId, to, again, onDone }: {
  patientId: string; to: string;
  /** The resident had this word before the Mark (false: they were blank, so "No" is new, not "again"). */
  again: boolean;
  onDone: (msg: string) => Promise<void> | void;
}) {
  const uid = useId().replace(/:/g, '');
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const key = useRef(newAtdKey());
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (!note.trim()) { setErr('Say why ATD is being undone.'); return; }
    setBusy(true); setErr(null);
    const r = await atdApi.change(patientId, { to_status: to, note: note.trim() }, key.current);
    setBusy(false);
    if (r.status !== undefined || r.success) key.current = newAtdKey();
    if (!r.success) { setErr(refusal(r)); return; }
    setOpen(false); setNote('');
    await onDone(`Undone: ATD is ${to}${again ? ' again' : ''}, and they have left ${LIST}.`);
  };
  return (
    <div className="atd-undo">
      <button type="button" className="atd-btn atd-btn--link" aria-expanded={open} aria-controls={`atd-undo-${uid}`} onClick={() => { setErr(null); setOpen(!open); }}>Undo</button>
      {open && (
        <form id={`atd-undo-${uid}`} className="atd-confirm atd-confirm--quiet" onSubmit={submit} aria-label="Undo Mark ATD">
          <p className="atd-quiet">Records ATD as {to} with your reason. The history keeps both.</p>
          <label className="atd-field" htmlFor={`atd-undo-note-${uid}`}><span>Reason (required)</span>
            <textarea id={`atd-undo-note-${uid}`} rows={2} value={note} onChange={(e) => setNote(e.target.value)} required />
          </label>
          {err && <p className="atd-err" role="alert">{err}</p>}
          <div className="atd-btns">
            <button type="submit" className="atd-btn atd-btn--primary" disabled={busy}>{busy ? 'Undoing…' : 'Undo ATD'}</button>
            <button type="button" className="atd-btn" onClick={() => setOpen(false)} disabled={busy}>Keep ATD</button>
          </div>
        </form>
      )}
    </div>
  );
}

function HistoryRow({ h }: { h: AtdHistoryRow }) {
  const who = h.source === 'MONDAY_BASELINE' ? 'From Monday at cutover' : h.actor ? `${h.actor.name || 'Unnamed user'}${h.actor.role ? ` (${h.actor.role})` : ''}` : 'Actor not recorded';
  return (
    <li>
      <strong>{dayOf(h.effective_date || h.at)}</strong> · {h.from || 'blank'} → {h.to} · {who}
      {h.note ? <span className="atd-note"> · “{h.note}”</span> : null}
    </li>
  );
}

const TONE: Record<string, 'yes' | 'done' | 'no'> = { Yes: 'yes', Dispensed: 'done' };

/**
 * `onChanged`: told after an act is recorded (and the panel re-read) with the message and the resident's ATD word now
 * (null: none; undefined: the re-read failed), so a list beside it can re-read its row and judge whether it still
 * matches. `openMark`: a number that, when it changes, opens the Mark ATD confirmation (the Finance Kanban's "Mark
 * ATD…" act); if the resident is already ATD the panel says so instead.
 */
export default function AtdPanel({ patientId, onChanged, openMark = null, card = false }: {
  patientId: string | null | undefined; onChanged?: (msg: string, status?: string | null) => void; openMark?: number | null;
  /** Drawn as its own card (the Finance Kanban's dock), rather than as a section inside one. */
  card?: boolean;
}) {
  const [d, setD] = useState<AtdResident | null>(null);
  const [hidden, setHidden] = useState(false);
  const [failure, setFailure] = useState<{ text: string; alert: boolean } | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [markOpen, setMarkOpen] = useState(false);
  /** The word the resident had before a Mark ATD made here: what Undo returns them to. */
  const [undoTo, setUndoTo] = useState<{ to: string; again: boolean } | null>(null);
  const current = useRef(patientId);
  current.current = patientId;

  const load = useCallback(async (): Promise<AtdResident | null> => {
    if (!patientId) return null;
    const r = await atdApi.resident(patientId);
    if (current.current !== patientId) return null;
    if (r.success && r.data) { setD(r.data); setFailure(null); setHidden(false); return r.data; }
    setD(null);
    if (r.status === 403) { setHidden(true); setFailure(null); return null; }
    setHidden(false);
    if (r.code === 'ATD_MIGRATION_REQUIRED') setFailure({ text: r.message || r.error || 'ATD is not set up yet.', alert: true });
    else if (r.code === 'NOT_FOUND' || r.status === 404) setFailure({ text: 'No ATD record is available for this resident.', alert: false });
    else setFailure({ text: r.status === undefined ? 'Could not reach the server, so ATD is not shown.' : (r.message || r.error || 'ATD could not be loaded.'), alert: false });
    return null;
  }, [patientId]);
  useEffect(() => { setD(null); setDone(null); setHidden(false); setFailure(null); setMarkOpen(false); setUndoTo(null); void load(); }, [load]);

  // A "Mark ATD…" asked from a board: open the confirmation once the record is read, or say it is already marked.
  const asked = useRef<number | null>(null);
  useEffect(() => {
    if (openMark === null || openMark === undefined || !d || asked.current === openMark) return;
    asked.current = openMark;
    if ((d.allowed_for_you || []).some((m) => m.to === 'Yes')) { setDone(null); setMarkOpen(true); return; }
    if (d.status === 'Yes') setDone(markResultWords({ unchanged: true, scheduling: d.scheduling, building: d.facility ? { id: d.facility.id, name: d.facility.name } : null }, d.atd_date));
    else setDone(d.can.mark ? `ATD is ${d.status || 'blank'} here; Mark ATD is not open from that word.` : 'Mark ATD is not open for you on this resident.');
  }, [openMark, d]);

  if (!patientId || hidden) return null;
  if (failure) {
    return (
      <section className={`atd-panel${card ? ' atd-panel--card' : ''}`} aria-label="ATD">
        <div className="atd-head"><h3>ATD <span className="atd-sub">approved to dispense</span></h3></div>
        {failure.alert ? <p className="atd-err" role="alert">{failure.text}</p> : <p className="atd-quiet">{failure.text}</p>}
      </section>
    );
  }
  if (!d) return <section className={`atd-panel${card ? ' atd-panel--card' : ''}`} aria-label="ATD"><div className="atd-head"><h3>ATD <span className="atd-sub">approved to dispense</span></h3></div><p className="atd-quiet">Loading…</p></section>;

  const mine = d.allowed_for_you || [];
  const canAct = (d.can.mark || d.can.dispense) && mine.length > 0;
  const yesMove = mine.find((m) => m.to === 'Yes');
  const dispMove = mine.find((m) => m.to === 'Dispensed');
  const others = mine.filter((m) => m.to !== 'Yes' && m.to !== 'Dispensed');
  const isYes = d.status === 'Yes';
  const s = d.scheduling;
  const reload = async (msg: string) => { setDone(msg); const fresh = await load(); if (onChanged) onChanged(msg, fresh ? fresh.status : undefined); return fresh; };
  const onDone = async (msg: string) => { setUndoTo(null); await reload(msg); };
  const onMarked = async (msg: string, out: AtdChangeOutcome | null) => {
    const fresh = await reload(msg);
    // Undo is offered for a Mark made HERE, and only while the governed reversal is open to this caller.
    const had = out && out.previous && ['No', 'REFUSED', 'REVERSAL'].includes(out.previous) ? out.previous : null;
    const back = out && out.changed ? had || 'No' : null;
    setUndoTo(back && fresh && (fresh.allowed_for_you || []).some((m) => m.to === back) ? { to: back, again: Boolean(had) } : null);
  };
  const tone = d.status ? TONE[d.status] || 'no' : 'none';

  return (
    <section className={`atd-panel${card ? ' atd-panel--card' : ''}`} aria-label="ATD">
      <div className="atd-head">
        <h3>ATD <span className="atd-sub">approved to dispense</span></h3>
        <span className={`atd-pill atd-pill--${tone}`}>
          {d.status ? <><strong>{d.status}</strong>{d.meaning ? ` — ${d.meaning}` : ''}</> : 'No ATD recorded'}
        </span>
      </div>
      {isYes && <p className="atd-state">Marked ATD{d.atd_date ? ` since ${d.atd_date}` : ''}{s ? ` · ${s.in_atd_list ? `in ${LIST}` : `not in ${LIST}`}` : ''}</p>}
      {!d.recognised && <p className="atd-state">&ldquo;{d.status}&rdquo; is a word eMerge does not recognise.</p>}
      {isYes && d.facility && d.facility.closure && <p className="atd-warn" role="note">{closedWords(d.facility.closure)}</p>}
      {isYes && !d.facility && <p className="atd-warn" role="note">No building is set, so Scheduling can&rsquo;t put them on a manifest. {SET_BUILDING}</p>}
      {d.merged_into_patient_id && <p className="atd-quiet">This resident record was merged into another; changes are made there.</p>}
      {done && (
        <div className="atd-ok">
          <p role="status">{done}</p>
          {undoTo && isYes && <UndoMark patientId={patientId} to={undoTo.to} again={undoTo.again} onDone={onDone} />}
        </div>
      )}

      {canAct ? (
        <div className="atd-actions">
          {yesMove && <MarkAtd key={`mark-${d.status}`} d={d} open={markOpen} setOpen={setMarkOpen} onDone={onMarked} />}
          {dispMove && <MoveForm key={`disp-${d.status}`} patientId={patientId} today={d.today} moves={[dispMove]} title="Mark dispensed" cta="Record dispensed" defaultToday onDone={onDone} />}
          {others.length > 0 && <MoveForm key={`oth-${d.status}`} patientId={patientId} today={d.today} moves={others} title="Other change…" cta="Record change" defaultToday={false} pickWord onDone={onDone} />}
        </div>
      ) : <p className="atd-quiet">Read-only: you can see ATD here but not change it.</p>}

      {isYes && (
        <div className="atd-told">
          <h4>Who was told</h4>
          {d.notices.length === 0 ? <p className="atd-quiet">No notice recorded for this Yes.</p> : (
            <ul>{d.notices.map((n, i) => <li key={i}>{NOTICE_WHO[n.kind]}: {noticeWords(n)}</li>)}</ul>
          )}
          {d.can.resend && <ResendNotices patientId={patientId} onDone={onDone} />}
          <p>Scheduling: {d.dispense_plan ? planWords(d.dispense_plan) : 'Not yet scheduled'}</p>
        </div>
      )}

      <div className="atd-history">
        <h4>History</h4>
        {d.history.length === 0 ? <p className="atd-quiet">No history recorded.</p> : <ul>{d.history.map((h) => <HistoryRow key={h.id} h={h} />)}</ul>}
      </div>
    </section>
  );
}
